import React, { useState } from 'react'
import { Download01, File01, File02, Image01 } from '@untitled-ui/icons-react'
import { download } from '@src/services/NetworkUtils'
import FileViewer from '@src/components/FileViewer'
import { useApp } from '@src/AppContext'
import { CREDIT_REPORT_PERMISSION } from '@src/services/Helper'

/**
 * @param {object} props
 * @param {object} props.personalDetail Response from GET /v1/workforce/:id?type=personal_information
 */

const getIcon = (type) => {
  switch (type.toUpperCase()) {
    case 'PDF':
      return <File02 className="h-4 w-4 text-red-600" />
    case 'JPG':
    case 'PNG':
      return <Image01 className="h-4 w-4 text-blue-600" />
    case 'GIF':
      return <File01 className="h-4 w-4 text-purple-600" />
    case 'DOC':
    case 'DOCX':
      return <File02 className="h-4 w-4 text-sky-600" />
    default:
      return <File01 className="h-4 w-4 text-gray-600" />
  }
}

const getPillColor = (type) => {
  switch (type.toUpperCase()) {
    case 'PDF':
      return 'bg-red-50 text-red-700'
    case 'JPG':
    case 'PNG':
      return 'bg-blue-50 text-blue-700'
    case 'GIF':
      return 'bg-purple-50 text-purple-700'
    case 'DOC':
    case 'DOCX':
      return 'bg-sky-50 text-sky-700'
    default:
      return 'bg-gray-50 text-gray-700'
  }
}

function deriveType(att) {
  // Real records ship mime_type and file_name; the demo rows ship `type`.
  if (att.type) return String(att.type).toUpperCase()
  if (att.mime_type) {
    if (att.mime_type.includes('pdf')) return 'PDF'
    if (att.mime_type.includes('jpeg') || att.mime_type.includes('jpg')) return 'JPG'
    if (att.mime_type.includes('png')) return 'PNG'
    if (att.mime_type.includes('gif')) return 'GIF'
    if (att.mime_type.includes('msword') || att.mime_type.includes('wordprocessingml')) return 'DOC'
  }
  if (att.file_name) {
    const ext = att.file_name.split('.').pop()
    if (ext) return ext.toUpperCase()
  }
  return 'FILE'
}

function deriveName(att) {
  return att.name ?? att.file_name ?? '—'
}

function deriveDate(att) {
  if (att.date) return att.date
  const v = att.uploaded_at ?? att.created_at
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// Resolve an attachment to an openable URL. KTP ships an absolute public URL
// (served by the backoffice /uploads proxy); the on-demand PDFs ship an
// API-relative path that `download()` turns into a `?token=`-authenticated URL.
function resolveAttachmentUrl(url) {
  if (!url) return ''
  return /^https?:\/\//i.test(url) ? url : download(url)
}

export default function AttachmentsCard({ personalDetail }) {
  const { hasPermission } = useApp()
  // Credit-report PDFs 403 without view_credit_report — don't list them.
  const canViewReport = hasPermission(...CREDIT_REPORT_PERMISSION)
  const attachments = (personalDetail?.attachments ?? []).filter(
    (a) => canViewReport || a.document_type !== 'credit_report'
  )
  // { url, fileName, mimeType } for the in-app preview, or null.
  const [viewerFile, setViewerFile] = useState(null)

  return (
    <>
      <div className="flex flex-col gap-[2px] rounded-xl border border-gray-200 bg-[#fdfdfd] shadow-sm overflow-hidden">
      {/* Heading wrapper */}
      <div className="flex items-center justify-between pl-5 pr-5 pt-3 pb-2">
        <h3 className="text-sm font-semibold text-[#181d27]">Attachments</h3>
      </div>

      {/* Table Container */}
      <div className="bg-white mx-[1px] mb-[1px] rounded-[12px] border border-[#e9eaeb] overflow-hidden shadow-sm">
        {attachments.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            No attachments available yet.
          </div>
        ) : (
          attachments.map((item, index) => {
            const type = deriveType(item)
            const clickable = Boolean(item?.url)
            return (
              <button
                type="button"
                key={item.id ?? index}
                disabled={!clickable}
                onClick={() =>
                  setViewerFile({
                    url: resolveAttachmentUrl(item.url),
                    fileName: deriveName(item),
                    mimeType: item.mime_type,
                  })
                }
                title={clickable ? `View ${deriveName(item)}` : undefined}
                className={`group flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
                  index !== attachments.length - 1 ? 'border-b border-gray-100' : ''
                } ${clickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${getPillColor(type)}`}>
                    {getIcon(type)}
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                    {deriveName(item)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 font-medium">
                    {deriveDate(item)}
                  </span>
                  {clickable && (
                    <Download01 className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
                  )}
                </div>
              </button>
            )
          })
        )}
      </div>
      </div>

      <FileViewer
        open={!!viewerFile}
        onClose={() => setViewerFile(null)}
        url={viewerFile?.url}
        fileName={viewerFile?.fileName}
        mimeType={viewerFile?.mimeType}
      />
    </>
  )
}

